import { useTranslation } from "react-i18next"

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@f2e/ui/components/base"

import { PendingOrganizationInvitation } from "@/hooks/api"

export interface PendingInvitationsSectionProps {
  invitations: PendingOrganizationInvitation[]
  isRevoking: boolean
  isResending: boolean
  onRevoke: (invitationId: string) => void
  onResend: (invitationId: string) => void
}

const PendingInvitationsSection = ({
  invitations,
  isRevoking,
  isResending,
  onRevoke,
  onResend,
}: PendingInvitationsSectionProps) => {
  const { t, i18n } = useTranslation()

  const formatDateTime = (value: string) =>
    new Date(value).toLocaleString(i18n.language)

  return (
    <div className="mt-8 space-y-3">
      <div>
        <h4 className="text-lg font-semibold">
          {t("settings.users.invitation.pending.title")}
        </h4>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t("settings.users.invitation.pending.description")}
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t("settings.users.invitation.preview.email")}
            </TableHead>
            <TableHead>
              {t("settings.users.invitation.preview.workspace")}
            </TableHead>
            <TableHead>
              {t("settings.users.invitation.pending.invitedBy")}
            </TableHead>
            <TableHead>
              {t("settings.users.invitation.pending.status")}
            </TableHead>
            <TableHead>
              {t("settings.users.invitation.pending.expiresAt")}
            </TableHead>
            <TableHead className="w-40" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {invitations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-slate-500">
                {t("settings.users.invitation.pending.empty")}
              </TableCell>
            </TableRow>
          ) : (
            invitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell>{invitation.email}</TableCell>
                <TableCell>{invitation.workspaceName}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div>{invitation.invitedByName}</div>
                    <div className="text-xs text-slate-500">
                      {invitation.invitedByEmail}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {t(
                    `settings.users.invitation.pending.statuses.${invitation.status}`
                  )}
                </TableCell>
                <TableCell>{formatDateTime(invitation.expiresAt)}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isResending}
                      onClick={() => onResend(invitation.id)}
                    >
                      {t("settings.users.invitation.pending.resend")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isRevoking || invitation.status !== "pending"}
                      onClick={() => onRevoke(invitation.id)}
                    >
                      {t("settings.users.invitation.pending.revoke")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default PendingInvitationsSection
